import React, { useEffect, useState, useRef } from "react";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import { functions } from "../../../constants/common-functions";
import { JWT } from "../../../constants/local-storage";
import UtilisationFloorPlanDeskInfoModal from "./UtilisationFloorPlanDeskInfoModal";

export default function UtilisationFloorPlanFloorPanel({}) {
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [initialScale, setInitialScale] = useState(1);
  const [imageUrl, setImageUrl] = useState("");
  const [ready, setReady] = useState(true);
  const [svgs, setSvgs] = useState([]);
  const [utilisation, setUtilisation] = useState({});
  const [modalOpen, setModalOpen] = useState(false);

  const containerRef = useRef(null);

  // Load floorplan meta
  useEffect(() => {
    setMeta(null);
    setErr("");
    if (!ready) return;

    setLoading(true);

    functions.getObjectFromAjax(
      `resolutionsoftwareplatform/survey/floorplan/meta?clientId=2&buildingId=13&floorId=14`,
      null,
      processMeta
    );
  }, [ready]);

  useEffect(() => {
    if (!meta) return;

    const token = localStorage.getItem(JWT);
    fetch(
      `resolutionsoftwareplatform/survey/floorplan/download?clientId=2&buildingId=13&floorId=14&t=${Date.now()}`,
      {
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include",
      }
    )
      .then((res) => {
        if (!res.ok) throw new Error("Download failed " + res.status);
        return res.blob();
      })
      .then((blob) => setImageUrl(URL.createObjectURL(blob)))
      .catch((err) => console.error(err));

    loadDesks();
  }, [meta]);

  const processMeta = (data) => {
    if (data.error) {
      setErr(data.error);
    } else {
      setMeta(data);
    }
    setLoading(false);
  };

  function loadDesks() {
    functions.getArrayFromAjax(
      "/resolutionsoftwareplatform/survey/workplacesforspace?spaceId=15",
      function (data) {
        const seeded =
          (data || [])
            .filter((d) => d.svg)
            .map((d) => ({
              path: d.svg,
              deskId: d.id,
            })) || [];
        setSvgs(seeded);
      },
      function (err) {
        console.error("Error loading desks:", err);
      }
    );
  }

  useEffect(() => {
    if (!svgs.length) return;

    const updated = svgs.map((s) => {
      const el = document.getElementById(`desk-path-${s.deskId}`);
      if (el) {
        const bbox = el.getBBox();
        return {
          ...s,
          cx: bbox.x + bbox.width / 2,
          cy: bbox.y + bbox.height / 2,
        };
      }
      return s;
    });

    setSvgs(updated);
  }, [svgs.length]);

  useEffect(() => {
    if (!svgs.length) return;

    const interval = setInterval(() => {
      const updated = {};
      svgs.forEach((d) => {
        updated[d.deskId] = Math.random() > 0.5 ? 1 : 0; // Simulate data toggle
      });
      setUtilisation(updated);
    }, 3000);

    return () => clearInterval(interval);
  }, [svgs]);

  useEffect(() => {
    if (!meta) return;
    const el = containerRef.current;
    if (!el) return;

    const calc = () => {
      const cw = el.clientWidth || 1;
      const ch = el.clientHeight || 1;
      const scale = Math.min(cw / meta.width, ch / meta.height);
      setInitialScale(scale > 0 ? scale : 1);
    };

    const ro = new ResizeObserver(() => calc());
    ro.observe(el);
    return () => ro.disconnect();
  }, [meta]);

  if (!ready) {
    return (
      <div style={{ color: "#999", fontSize: 13 }}>
        Select client  location  building  floor  space to view the plan.
      </div>
    );
  }
  if (loading) return <div>Loading plan</div>;
  if (err) return <div style={{ color: "crimson" }}>{err}</div>;
  if (!meta) return null;
  
  const goDesk = () => {
  	setModalOpen(true)
  };

  return (
    <div
      ref={containerRef}
      style={{ width: "100%", height: "100%", overflow: "hidden" }}
    >
      <TransformWrapper
        initialScale={initialScale}
        minScale={0.01}
        maxScale={8}
        wheel={{ step: 0.2 }}
        doubleClick={{ disabled: true }}
        pinch={{ step: 5 }}
        centerOnInit
      >
        <TransformComponent wrapperStyle={{ width: "100%", height: "100%" }}>
          <svg
            width={meta.width}
            height={meta.height}
            style={{
              border: "0px solid #ccc",
              cursor: "crosshair",
              display: "block",
            }}
          >
            <defs>
              <filter
                id="gaussianBlur"
                x="-50%"
                y="-50%"
                width="200%"
                height="200%"
              >
                <feGaussianBlur in="SourceGraphic" stdDeviation="20" />
              </filter>
            </defs>

            {/* Base image */}
            <image
              href={imageUrl}
              x="0"
              y="0"
              width={meta.width}
              height={meta.height}
              preserveAspectRatio="xMinYMin meet"
            />

            {/* Desk outlines */}
            {svgs.map((shape, index) => (
              <path
                key={`desk-${index}`}
                id={`desk-path-${shape.deskId}`}
                d={shape.path}
                fill="none"
                stroke="red"
                strokeWidth="1"
              />
            ))}

            {/* Gaussian bubbles */}
            {svgs.map((shape, index) => {
						  const isUsed = utilisation[shape.deskId] === 1;
						  if (!isUsed || !shape.cx || !shape.cy) return null;
						
						  // Dynamic radius and color based on utilisation
						  const radius = 50; // Fixed larger radius for utilised desks
						  const opacity = isUsed ? 0.8 : 0.1; // More intense for used desks, faint for unused
						  const fill = isUsed
						    ? `rgba(255, ${255 - Math.random() * 255}, 0, ${opacity})` // Red  Green with opacity
						    : `rgba(0, 0, 0, ${opacity})`; // Shadow for unused desks
						
						  return (
						    <circle
						      key={`bubble-${index}`}
						      cx={shape.cx}
						      cy={shape.cy}
						      r={radius}
						      fill={fill}
						      filter="url(#gaussianBlur)"
						      onClick={() => {goDesk();}}
						    />
						  );
						})}
          </svg>
        </TransformComponent>
      </TransformWrapper>
      <UtilisationFloorPlanDeskInfoModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
      />
    </div>
  );
}




